import { useMemo, useState } from "react";
import { AuditReportGrain } from "../models/auditReports";
import {
  AuditReportPeriodSelection,
  AuditReportPreset,
  AuditReportPresetKey,
  CUSTOM_PERIOD,
  initialPresetFor,
  presetsForReport,
  resolveAuditReportPreset,
} from "../utils/constants/audit-report-constants";
import {
  getMaxReportDateTo,
  getMinReportDateFrom,
  isReportDateRangeWithinLimit,
} from "../utils/constants/report-date-range";

/**
 * Estado de la fila de períodos (última semana / último mes / últimos 6 meses /
 * personalizado), compartido por TODOS los reportes de la pantalla.
 *
 * Vive acá y no en cada pantalla porque los botones son la misma decisión en
 * todas: el export de eventos los tenía distintos solo porque se escribió antes
 * que el resto, y eso se notaba al cambiar de tipo en el selector.
 *
 * `grains` es lo único que cambia entre reportes: el botón setea rango Y
 * agrupamiento juntos, así que un reporte que solo se calcula por mes no ofrece
 * "última semana". Un reporte que no agrupa nada —el export de eventos, que es
 * una fila por evento— pasa todos los granos y simplemente ignora `grain`.
 */
const ALL_GRAINS: AuditReportGrain[] = ["month", "week"];

const startOfToday = (): Date => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  return date;
};

export interface ReportPeriod {
  /** Hoy a las 00:00, del navegador del usuario. Tope de los date pickers. */
  today: Date;
  /** Presets que se muestran, ya filtrados por los granos del reporte. */
  presets: AuditReportPreset[];
  period: AuditReportPeriodSelection;
  /** Agrupamiento que dejó el preset elegido. Los reportes sin bucket lo ignoran. */
  grain: AuditReportGrain;
  dateFrom: Date;
  dateTo: Date;
  /** Si el rango es consultable: no invertido y dentro del tope de meses. */
  rangeIsValid: boolean;
  applyPreset: (preset: AuditReportPresetKey) => void;
  enableCustomPeriod: () => void;
  handleDateFromChange: (date: Date | null) => void;
  handleDateToChange: (date: Date | null) => void;
}

export const useReportPeriod = (grains: AuditReportGrain[] = ALL_GRAINS): ReportPeriod => {
  const today = useMemo(startOfToday, []);

  const presets = useMemo(() => presetsForReport(grains), [grains]);
  const initialPreset = useMemo(() => initialPresetFor(grains), [grains]);

  const [grain, setGrain] = useState<AuditReportGrain>(initialPreset.grain);
  // Qué botón está elegido. Arranca en un preset, así que los selectores de
  // fecha no se muestran hasta que se pida "Personalizado".
  const [period, setPeriod] = useState<AuditReportPeriodSelection>(initialPreset.key);

  const initialRange = useMemo(
    () => resolveAuditReportPreset(initialPreset.key, today),
    [initialPreset, today]
  );
  const [dateFrom, setDateFrom] = useState<Date>(initialRange.dateFrom);
  const [dateTo, setDateTo] = useState<Date>(initialRange.dateTo);

  /** El botón setea rango y grano juntos: es lo que los hace coherentes. */
  const applyPreset = (preset: AuditReportPresetKey) => {
    const option = presets.find((candidate) => candidate.key === preset);
    if (!option) {
      return;
    }

    const range = resolveAuditReportPreset(preset, today);
    setPeriod(preset);
    setGrain(option.grain);
    setDateFrom(range.dateFrom);
    setDateTo(range.dateTo);
  };

  /**
   * Personalizado no cambia el rango ni el grano: abre los selectores con lo que
   * ya se estaba viendo, para editar desde ahí en vez de arrancar de cero.
   */
  const enableCustomPeriod = () => setPeriod(CUSTOM_PERIOD);

  const handleDateFromChange = (date: Date | null) => {
    if (!date) return;
    setDateFrom(date);
    const maxTo = getMaxReportDateTo(date, today);
    if (dateTo.getTime() > maxTo.getTime()) {
      setDateTo(maxTo);
    }
    if (dateTo.getTime() < date.getTime()) {
      setDateTo(date);
    }
  };

  const handleDateToChange = (date: Date | null) => {
    if (!date) return;
    setDateTo(date);
    const minFrom = getMinReportDateFrom(date);
    if (dateFrom.getTime() < minFrom.getTime()) {
      setDateFrom(minFrom);
    }
    if (dateFrom.getTime() > date.getTime()) {
      setDateFrom(date);
    }
  };

  const rangeIsValid =
    dateFrom.getTime() <= dateTo.getTime() && isReportDateRangeWithinLimit(dateFrom, dateTo);

  return {
    today,
    presets,
    period,
    grain,
    dateFrom,
    dateTo,
    rangeIsValid,
    applyPreset,
    enableCustomPeriod,
    handleDateFromChange,
    handleDateToChange,
  };
};
